import type { SiteConfig } from '../types/cms';

/**
 * CMS Configuration
 * -----------------
 * All site content is centralized here.
 * To integrate with a headless CMS (WordPress REST API, Strapi, Sanity, etc.):
 *   1. Fetch data from your CMS endpoint
 *   2. Map the response to the SiteConfig interface
 *   3. Replace or merge with this default config
 */

export const defaultSiteConfig: SiteConfig = {
  site: {
    title: '愉亮國際',
    description: '雙模式一站式製作 - AI 數字人短視頻 + 專業實拍自由搭配，按需組合打造完整品牌故事',
    logo: '愉亮國際',
    logoIcon: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/assets/logo-icon.jpg',
    logoFull: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/assets/logo-full.jpg',
  },

  navigation: [
    { id: 'dual-mode', label: '雙模式製作', href: '#dual-mode' },
    { id: 'pain-points', label: '痛點分析', href: '#pain-points' },
    { id: 'services', label: '核心服務', href: '#services' },
    { id: 'ai-feature', label: '個人 IP', href: '#ai-feature' },
    { id: 'portfolio', label: '作品集', href: '#portfolio' },
    { id: 'business-scope', label: '業務範圍', href: '#business-scope' },
    { id: 'pricing', label: '收費方案', href: '#pricing' },
    { id: 'contact', label: '聯絡我們', href: '#contact' },
  ],

  hero: {
    badge: '雙模式一站式製作',
    headline: '實體商家',
    highlight: '救星',
    video: '',
    subheadline: 'AI 數字人短視頻 + 專業實拍自由搭配，按需組合打造完整品牌故事',
    features: ['AI 數字人短視頻', '專業實拍', '文案策劃', '跨平台發布', '長期陪跑'],
    ctaText: '📲 免費診斷你的短視頻方案',
    ctaSubtext: '限時免費 · WhatsApp 即時回覆',
    whatsappMessage: '你好李小姐！我係老闆，想了解雙模式短視頻拍攝推廣服務 🎬',
    backgroundImage: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/assets/hero-bg.jpg',
  },

  dualMode: {
    sectionTitle: '雙模式一站式製作',
    subtitle: 'AI 數字人短視頻 + 專業實拍自由搭配，按需組合打造完整品牌故事',
    modes: [
      {
        id: 'ai-avatar',
        icon: 'Bot',
        title: 'AI 數字人短視頻',
        tagline: '免出鏡 · 免場地 · 快速產出',
        description: 'AI 技術打破時間、成本、場地限制，唔使老闆出鏡，短時間內批量產出高頻內容，適合快速起步同持續曝光。',
      },
      {
        id: 'real-shoot',
        icon: 'Camera',
        title: '專業實拍製作',
        tagline: '真實場景 · 品牌故事 · 質感呈現',
        description: '專業攝影團隊親臨上門取景，還原品牌真實故事與產品質感，打造有溫度、有信任感的深度內容。',
      },
    ],
    missionLabel: '我們的使命',
    missionText: '透過專業實拍還原品牌真實故事，藉 AI 技術打破時間、成本、場地限制，翻轉內容創作的各種不可能，協助每位客戶綻放專屬的營運奇蹟。',
    ctaText: '了解雙模式製作方案',
    whatsappMessage: '你好李小姐！想了解 AI 數字人短視頻同專業實拍雙模式製作服務可以點樣自由搭配 🎬',
  },

  painPoints: {
    sectionTitle: '你是否遇到這些問題？',
    subtitle: '別讓好產品輸在不會展示',
    intro: '全流程專業團隊出擊，解決拍片、寫稿、運營所有難題，輕鬆收穫穩定客源',
    items: [
      {
        id: 'traffic',
        problem: '客源少',
        problemDesc: '到店客流少，曝光不穩，生意難做',
        solution: '自動發短視頻',
        solutionDesc: '精準曝光引流，自動將宣傳片發出',
      },
      {
        id: 'content',
        problem: '內容弱',
        problemDesc: '宣傳素材弱，拍剪效率低，賣點不清',
        solution: '雙模式雙製作',
        solutionDesc: 'AI 數字人短視頻 + 上門實拍，真實質感與效率兼備',
      },
      {
        id: 'conversion',
        problem: '轉化低',
        problemDesc: '好評獲取慢，轉化鏈路長，諮詢不足',
        solution: '全流程陪跑',
        solutionDesc: '文案策劃 + 長期營運陪跑，1個月內看見變化',
      },
    ],
    solutionTitle: '專業團隊這樣解決',
    ctaText: '馬上預約免費診斷',
    whatsappMessage: '你好李小姐！我想了解點樣解決我門店嘅曝光同轉化問題 🎬',
  },

  services: {
    sectionTitle: '核心服務項目',
    subtitle: '匯集 AI 數字人、商業攝影、跨平台行銷專家，為你打造全域獲客體系',
    intro: '業務範圍涵蓋 AI 數字人短視頻、專業實拍製作、跨平台文案策劃、短視頻營運培訓陪跑、抖音藍 V 上架、團購券上架',
    services: [
      {
        id: 'acquisition',
        icon: 'Clapperboard',
        title: '實體店長期獲客短視頻推廣',
        tagline: '雙製作模式 · 長期陪跑',
        description: '融合 AI 數字人短視頻與專業實拍雙製作模式，配套文案策劃與長期營運陪跑，穩定輸出線上流量，為門店實現持續拓客。',
        image: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/ai-customer-acquisition-1.jpg',
        tags: ['AI 數字人短視頻', '專業實拍', '文案策劃', '長期陪跑'],
      },
      {
        id: 'blue-v',
        icon: 'BadgeCheck',
        title: '抖音藍 V 認證及團購券上架',
        tagline: '一站式辦理 · 成交閉環',
        description: '一站式辦理藍 V 認證、店鋪頁面搭建、團購套餐規劃與優惠券上架，完整搭建從線上引流到到店消費的成交閉環。',
        image: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/douyin-listing.jpg',
        tags: ['藍 V 認證', '店鋪搭建', '團購規劃', '優惠券上架'],
      },
      {
        id: 'personal-ip',
        icon: 'Fingerprint',
        title: '專屬個人 IP 打造',
        tagline: '數字人形象 · 記憶點鮮明',
        description: '提供數字人形象定製、品牌短視頻拍攝、多平台腳本策劃與帳號長期運營服務，打造記憶點鮮明的獨特個人品牌 IP。',
        image: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/personal-ip-new.jpg',
        tags: ['數字人定製', '品牌短視頻', '腳本策劃', '帳號運營'],
      },
      {
        id: 'kol',
        icon: 'Users',
        title: 'KOL 探店服務',
        tagline: '達人資源 · 口碑曝光',
        description: '整合本地各層級探店達人資源，一站式負責達人對接、腳本策劃、拍攝發布與流量助推，依託達人口碑曝光快速提升門店客流與團購轉化。',
        image: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/kol-new.jpg',
        tags: ['達人對接', '腳本策劃', '拍攝發布', '流量助推'],
      },
    ],
  },

  aiFeature: {
    sectionTitle: '老闆不會拍短視頻，也能做個人 IP',
    subtitle: '對上鏡有恐懼｜說話不夠順暢｜動作僵硬？',
    description: '你不是不會做 IP，只是不想反復出鏡、反復背稿，反復拍攝反復 NG還有花太多時間剪片',
    fears: ['怕上鏡緊張', '怕講話不順', '怕動作僵硬'],
    solution: 'AI 數字人幫你完成',
    capabilities: ['數字人形象定製', '多場景應用', '團隊拍攝', '文案 + 剪片', '品牌形象包裝'],
    ctaText: '限時免費診斷你的 AI 短視頻方案',
    whatsappMessage: '你好李小姐！我想了解 AI 數字人幫我做個人 IP嘅方案 🤖',
  },

  teamExpertise: {
    sectionTitle: '全流程專業團隊出擊',
    subtitle: '解決拍片、寫稿、運營所有難題，輕鬆收穫穩定客源',
    description: '匯集 AI 數字人、商業攝影、跨平台行銷、落地陪跑資深專家，融合前沿技術與影像實力，為科技品牌搭建全域獲客體系，書寫屬於行業的數字奇蹟。',
    expertise: [
      { id: 'ai-digital', icon: 'Bot', label: 'AI 數字人專家' },
      { id: 'photography', icon: 'Camera', label: '商業攝影' },
      { id: 'marketing', icon: 'Share2', label: '跨平台行銷' },
      { id: 'coaching', icon: 'Handshake', label: '落地陪跑' },
    ],
    closingLine: '融合前沿技術與影像實力，書寫屬於行業的數字奇蹟',
  },

  portfolio: {
    sectionTitle: '作品集展示',
    subtitle: '各行各業實體商家成功案例，睇下我們點樣幫佈局內容',
    platforms: [
      { id: 'all', label: '全部' },
      { id: 'facebook', label: 'Facebook' },
      { id: 'instagram', label: 'Instagram' },
      { id: 'xiaohongshu', label: '小紅書' },
      { id: 'douyin', label: '抖音' },
      { id: 'video', label: '視頻號' },
    ],
    items: [
      {
        id: 'hotpot',
        title: '火鍋店｜到店引流企劃',
        platform: 'douyin',
        views: '86.5K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/hotpot.jpg',
        href: '#',
      },
      {
        id: 'cafe',
        title: '咖啡店｜品牌故事短視頻',
        platform: 'instagram',
        views: '124K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/cafe.jpg',
        href: '#',
      },
      {
        id: 'boutique',
        title: '時裝店｜新品上架推廣',
        platform: 'xiaohongshu',
        views: '95.3K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/boutique.jpg',
        href: '#',
      },
      {
        id: 'salon',
        title: '美容院｜服務體驗紀錄',
        platform: 'facebook',
        views: '68.1K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/salon.jpg',
        href: '#',
      },
      {
        id: 'drinks',
        title: '飲品店｜新品試飲直拍',
        platform: 'video',
        views: '152K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/drinks.jpg',
        href: '#',
      },
      {
        id: 'bakery',
        title: '甜品店｜製作過程展示',
        platform: 'douyin',
        views: '203K',
        thumbnail: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/portfolio/bakery.jpg',
        href: '#',
      },
    ],
    ctaText: '查看更多作品',
    whatsappMessage: '你好李小姐！我睇完你們嘅作品集，想了解更多合作詳情 📹',
  },

  businessScope: {
    sectionTitle: '服務諮詢',
    subtitle: '業務範圍',
    companyName: '愉亮國際有限公司',
    items: [
      { id: 'ai-video', icon: 'Bot', label: 'AI 數字人短視頻' },
      { id: 'real-shoot', icon: 'Camera', label: '專業實拍製作' },
      { id: 'copywriting', icon: 'FileText', label: '跨平台文案策劃' },
      { id: 'coaching', icon: 'GraduationCap', label: '短視頻營運培訓陪跑' },
      { id: 'blue-v', icon: 'BadgeCheck', label: '抖音藍 V 上架' },
      { id: 'coupon', icon: 'Ticket', label: '團購券上架' },
    ],
    ctaText: '諮詢業務詳情',
    whatsappMessage: '你好李小姐！想諮詢你們嘅完整業務範圍同服務內容 📋',
  },

  pricing: {
    sectionTitle: '彈性套餐組合',
    subtitle: '依照預算、營業階段靈活搭配方案，從起步到成熟持續穩定拓客',
    intro: '新店入門 AI 流量套餐、品牌升級實拍形象套餐、長期經營黃金組合套餐，歡迎 WhatsApp 免費報價',
    plans: [
      {
        id: 'starter',
        name: '新店入門 AI 流量套餐',
        stage: '起步階段',
        priceLabel: '洽詢報價',
        unit: '/ 月',
        recommended: false,
        tagline: '低成本起步，AI 快速產出曝光',
        features: ['AI 數字人短視頻每月 4 條', '基礎文案策劃', '單平台發布', '基礎數據報告'],
        whatsappMessage: '你好李小姐！我想了解新店入門 AI 流量套餐嘅詳情同報價 💬',
      },
      {
        id: 'brand-upgrade',
        name: '品牌升級實拍形象套餐',
        stage: '升級階段',
        priceLabel: '洽詢報價',
        unit: '/ 月',
        recommended: true,
        tagline: '專業實拍，品牌形象全面升級',
        features: ['每月上門實拍 2 次', 'AI 數字人短視頻 8 條', '3 大平台矩陣發布', '好評自動獲取', '每月數據優化報告'],
        whatsappMessage: '你好李小姐！我想了解品牌升級實拍形象套餐嘅詳情同報價 🔥',
      },
      {
        id: 'golden-combo',
        name: '長期經營黃金組合套餐',
        stage: '成熟階段',
        priceLabel: '洽詢報價',
        unit: '/ 月',
        recommended: false,
        tagline: 'AI + 實拍雙模式，長期穩定拓客',
        features: ['每週上門實拍', 'AI 數字人短視頻無限條數', '全平台矩陣同步發布', '專屬個人 IP打造', '抖音藍 V + 團購券上架', '專屬顧問一對一優化'],
        whatsappMessage: '你好李小姐！我想了解長期經營黃金組合套餐嘅詳情同報價 👑',
      },
    ],
  },

  contact: {
    sectionTitle: '聯絡我們',
    subtitle: '掃描 QR Code 或點擊按鈕，即刻同李小姐傾傾你嘅方案',
    whatsapp: '85298239693',
    whatsappDisplay: '+852 9823 9693',
    wechat: 'w98239693',
    consultant: '李小姐',
    companyName: '愉亮國際有限公司',
    companyNameEn: 'Coaky International Co. Limited',
    defaultMessage: '你好李小姐！我係老闆，想了解雙模式短視頻拍攝推廣服務 🎬',
    qrCode: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/assets/whatsapp-qr.png',
    logo: 'https://pub-7621d740c6ae47c6b60a07f7c8398679.r2.dev/assets/logo-icon.jpg',
  },

  footer: {
    copyright: '© 2026 愉亮國際有限公司 Coaky International Co. Limited. All rights reserved.',
    logo: 'logo-full.jpg',
    socials: [
      { id: 'facebook', platform: 'Facebook', url: '#', icon: 'Facebook' },
      { id: 'instagram', platform: 'Instagram', url: '#', icon: 'Instagram' },
      { id: 'xiaohongshu', platform: '小紅書', url: '#', icon: 'BookOpen' },
      { id: 'douyin', platform: '抖音', url: '#', icon: 'Music2' },
      { id: 'video', platform: '視頻號', url: '#', icon: 'Video' },
    ],
  },
};
